import * as React from 'react'
import { connect } from "react-redux";
import { Redirect, RouteComponentProps, WithRouterProps } from 'react-router';
import { Route, Switch, withRouter } from 'react-router-dom';
import { createGlobalStyle, StyleSheetManager } from 'styled-components';
import ERoute from '../../ERoute';
import Article from './Article';
import NewsItem from './NewsItem';
import Conversation from './Conversation';
import { WidgetClientEvent, WidgetClientEventType, WidgetMessengerEventType, WidgetSpaceType } from '../../../types';
import { AppState } from '../../store';
import { ApplicationState } from '../../store/application/types';
import { setHostWindowSize, setWindowMaximized } from '../../store/application/actions';
import { MessageHelper } from '../../helpers';
import Container from '../../components/Messenger/Container';
import BottomTabs from './BottomTabs';
import RouteHelper from '../../../helpers/RouteHelper';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
  }
`

type IProps = { head?: HTMLHeadElement } & RouteComponentProps & IStateToProps & IDispatchToProps

interface IStateToProps {
  application: ApplicationState
}

interface IDispatchToProps {
  setHostWindowSize: typeof setHostWindowSize
  setWindowMaximized: typeof setWindowMaximized
}

class MessengerLayout extends React.Component<IProps> {
  constructor(props: IProps) {
    super(props)

    this.onMessageReceive = this.onMessageReceive.bind(this);
  }

  componentDidMount(): void {
    const { application: { messengerFrame }, head } = this.props

    // Listen to messages from the host
    messengerFrame?.contentWindow?.addEventListener('message', this.onMessageReceive);

    // Send message to the client that the widget is initialized
    MessageHelper.sendMessage({ type: WidgetMessengerEventType.INITIALIZED })

    // Manually inject head elements into iframe head
    if (head) {
      this.injectHeadElements(head);
    }
  }

  componentWillUnmount(): void {
    const { application: { messengerFrame } } = this.props

    // Remove message listener
    messengerFrame?.contentWindow?.removeEventListener('message', this.onMessageReceive);
  }

  onMessageReceive(event: MessageEvent<WidgetClientEvent>) {
    if (!event?.data?.type) return

    switch (event?.data?.type) {
      case WidgetClientEventType.WINDOW_RESIZE:
        this.props.setHostWindowSize(event.data.width, event.data.height)
        break
      case WidgetClientEventType.MAXIMIZE_WINDOW:
        this.props.setWindowMaximized(true)
        break
      case WidgetClientEventType.MINIMIZE_WINDOW:
        this.props.setWindowMaximized(false)
        break
      case WidgetClientEventType.NAVIGATE:
        this.onNavigateToSpace(event.data.space)
        break
      case WidgetClientEventType.SHOW_NEW_CONVERSATION:
        this.props.history.push(RouteHelper.process(ERoute.PATH_NEW_CONVERSATION))
        break
      case WidgetClientEventType.SHOW_CONVERSATION:
        this.props.history.push(RouteHelper.process(ERoute.PATH_CONVERSATION, { id: event.data.conversationId }))
        break
      case WidgetClientEventType.SHOW_ARTICLE:
        this.props.history.push(RouteHelper.process(ERoute.PATH_ARTICLE, { id: event.data.articleId }))
        break
      case WidgetClientEventType.SHOW_NEWS:
        this.props.history.push(RouteHelper.process(ERoute.PATH_NEWS, { id: event.data.newsId }))
        break

    }
  }

  onNavigateToSpace(space: WidgetSpaceType) {
    switch (space) {
      case WidgetSpaceType.HOME:
        this.props.history.push(ERoute.PATH_ROOT)
        break
      case WidgetSpaceType.CONVERSATIONS:
        this.props.history.push(ERoute.PATH_CONVERSATIONS)
        break
      case WidgetSpaceType.HELP:
        this.props.history.push(ERoute.PATH_HELP)
        break
      case WidgetSpaceType.NEWS:
        this.props.history.push(ERoute.PATH_NEWS_OVERVIEW)
    }
  }

  injectHeadElements(head: HTMLHeadElement) {
    // Create and inject preconnect links
    const preconnectGoogle = document.createElement('link');
    preconnectGoogle.rel = 'preconnect';
    preconnectGoogle.href = 'https://fonts.googleapis.com';
    head.appendChild(preconnectGoogle);

    const preconnectGstatic = document.createElement('link');
    preconnectGstatic.rel = 'preconnect';
    preconnectGstatic.href = 'https://fonts.gstatic.com';
    preconnectGstatic.crossOrigin = 'anonymous';
    head.appendChild(preconnectGstatic);

    // Create and inject font stylesheet
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap';
    fontLink.rel = 'stylesheet';
    head.appendChild(fontLink);

    // Create and inject viewport meta tag
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    head.appendChild(viewportMeta);
  }

  render() {
    const { head, application } = this.props

    if (!head) return null

    return (
      <StyleSheetManager target={head}>
        <>
          <GlobalStyle />
          <Container>
            <Switch>
              <Route path={ERoute.PATH_ARTICLE} component={Article} />
              <Route path={ERoute.PATH_NEWS} component={NewsItem} />
              <Route path={ERoute.PATH_NEW_CONVERSATION} component={Conversation} />
              <Route path={ERoute.PATH_CONVERSATION} component={Conversation} />
              <Route path={ERoute.PATH_ROOT} component={BottomTabs} />
              <Redirect to={ERoute.PATH_ROOT} />
            </Switch>
          </Container>
        </>
      </StyleSheetManager>
    );
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const { application } = state

  return {
    application: application
  }
}

const mapDispatchToProps: IDispatchToProps = {
  setHostWindowSize,
  setWindowMaximized
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MessengerLayout) as any) as any